import React from 'react';
import { Box, Input, Slider, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CONTROL_SLIDER_SIZES, getControlTokens } from './controlTokens';

function ControlSlider({
  context = 'filterBar',
  label,
  labelLines,
  value,
  min,
  max,
  onChange,
  onInputChange,
  onBlur,
  width = CONTROL_SLIDER_SIZES.width,
  labelWidth = CONTROL_SLIDER_SIZES.labelWidth,
  sliderWidth = CONTROL_SLIDER_SIZES.trackWidth,
  icon,
  inputAriaLabel = 'Slider value',
  sx,
}) {
  const theme = useTheme();
  const tokens = getControlTokens(theme, context);
  const controlWidth = typeof width === 'number' ? `${width}px` : width;
  const resolvedLabelWidth = typeof labelWidth === 'number' ? `${labelWidth}px` : labelWidth;
  const resolvedSliderWidth = typeof sliderWidth === 'number' ? `${sliderWidth}px` : sliderWidth;
  const resolvedLabelLines = Array.isArray(labelLines) && labelLines.length > 0
    ? labelLines
    : label ? [label] : [];
  const hasLabel = resolvedLabelLines.length > 0;

  return (
    <Box
      data-control-context={context}
      data-control-kind="slider"
      sx={{
        height: tokens.height,
        width: controlWidth,
        minWidth: controlWidth,
        maxWidth: '100%',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: tokens.surface,
        border: `1px solid ${tokens.borderColor}`,
        borderRadius: `${tokens.radius}px`,
        overflow: 'hidden',
        boxSizing: 'border-box',
        ...sx,
      }}
    >
      {hasLabel && (
        <Typography
          component="div"
          sx={{
            width: resolvedLabelWidth,
            height: '100%',
            px: 0.5,
            color: tokens.textColor,
            fontSize: tokens.labelFontSize,
            lineHeight: 1.05,
            textAlign: 'center',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {resolvedLabelLines.map((line) => (
            <Box
              component="span"
              key={line}
              sx={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {line}
            </Box>
          ))}
        </Typography>
      )}
      <Box
        sx={{
          height: tokens.height - 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          px: 1,
          borderLeft: hasLabel ? `1px solid ${tokens.dividerColor}` : undefined,
          flex: 1,
          minWidth: 0,
        }}
      >
        {icon}
        <Slider
          sx={{
            width: resolvedSliderWidth,
            flex: sliderWidth === '100%' ? '1 1 auto' : '0 0 auto',
            '& .MuiSlider-thumb': {
              width: CONTROL_SLIDER_SIZES.thumbSize,
              height: CONTROL_SLIDER_SIZES.thumbSize,
            },
          }}
          value={value}
          onChange={onChange}
          min={min}
          max={max}
          aria-label={inputAriaLabel}
        />
        <Input
          value={value}
          size="small"
          onChange={onInputChange}
          onBlur={onBlur}
          inputProps={{
            step: 1,
            min,
            max,
            type: 'text',
            pattern: '\\d*',
            'aria-label': inputAriaLabel,
          }}
          sx={{
            px: 1,
            color: theme.palette.text.secondary,
            height: tokens.height - 4,
            fontSize: tokens.fontSize,
            width: 30,
            flexShrink: 0,
            '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': { display: 'none' },
          }}
        />
      </Box>
    </Box>
  );
}

export default ControlSlider;
